import type { Country, request } from '@repo-level/core-front';
import { factory, Payment, snakify } from '@repo-level/core-front';

import type { CurrencyCode } from '../helpers/currency';
import type { TransactionState } from '../modules/cashbox/components/helpers';

// SUBSCRIPTIONS

export interface Subscriptions {
  receivePromos: boolean;
  receiveSmsPromos: boolean;
}

export type SubscriptionParams = Partial<Subscriptions>;

export const getProfileSubscriptions = factory(
  (r) => () => r<Subscriptions>('get', 'subscriptions')
);

export const updateProfileSubscriptions = factory(
  (r) => (subscriptionParams: SubscriptionParams) =>
    r<void>(
      'put',
      'subscriptions',
      snakify({ subscription_params: subscriptionParams })
    )
);

// ACCOUNTS

// TODO: REUSE FROM @core-front

export interface Account {
  currency: CurrencyCode;
  amountCents: number;
  availableToCashoutCents: number;
  availableToBetWithLockedCents: number;
  // hidden_at: null;  // type?
  // deposit_available: boolean;
  // selected_lottery_id: null;  // type?
  // wager_status: string;
  active: boolean;
}

export const getPlayerAccounts = factory(
  (r) => () => r<Account[]>('get', 'player/accounts?compatibility=false')
);

// DOCUMENTS

export enum DescriptionRecord {
  Identity = 'identity:',
  Address = 'address:',
  Payment = 'payment:',
}

export enum EDocumentApiType {
  Identity = 'identity',
  Address = 'address',
  Deposit = 'deposit',
  PaymentMethod = 'payment_method',
  Other = 'other',
}

export const DOCUMENT_API_TYPE: Record<DescriptionRecord, EDocumentApiType> = {
  [DescriptionRecord.Identity]: EDocumentApiType.Identity,
  [DescriptionRecord.Address]: EDocumentApiType.Address,
  [DescriptionRecord.Payment]: EDocumentApiType.PaymentMethod,
};

export enum DocumentStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  NOT_APPROVED = 'not_approved',
}

export interface Document {
  id: number;
  description: DescriptionRecord;
  documentType?: DocumentType;
  status: DocumentStatus;
  createdAt: string;
  updatedAt: string;
  fileName: string;
  declineReason?: string;
}

export const uploadDocument = factory(
  (r) => (documents: FormData) =>
    r<Document>('post', 'player/documents', documents)
);

export const getDocuments = factory(
  (r) => () => r<Document[]>('get', 'player/documents')
);

export const removeDocument = factory(
  (r) => (id: number) => r<void>('delete', `player/documents/${id}`)
);

// SESSIONS

export interface Session {
  id: number;
  ip: string;
  country: Country;
  userAgent: string;
  createdAt: string;
  current: boolean | null;
}

export const getSessions = factory(
  (r) => () => r<Session[]>('get', 'player/sessions')
);

export interface ClosedSession extends Session {
  closedAt: string;
}

export const getSessionsClosed = factory(
  (r) => () => r<ClosedSession[]>('get', 'player/sessions/closed')
);

export const removeSession = factory(
  (r) => (id: number) => r<void>('delete', `player/sessions/${id}`)
);

// USER

export interface UpdatePasswordBody {
  currentPassword: string;
  email: string;
  password: string;
  passwordConfirmation: string;
}

export const updatePassword = factory(
  (r) => (passwords: UpdatePasswordBody) =>
    r<{ errors: unknown }>('put', 'users', { user: snakify({ ...passwords }) })
);

// Retries disabled: a lost 5xx response may already have queued the report
export const requestGdprReport = factory(
  (r) => () =>
    r<void>('post', 'gdpr_reports', undefined, undefined, undefined, 0)
);

export const emailConfirmation = factory(
  (r) => (email: string) =>
    r<void>('post', 'users/confirmation', { user: { email } })
);

// player/two_factor

export const getTwoFactor = factory(
  (r) => () =>
    r<{
      data: string;
      otpSecret: string;
    }>('get', 'v2/player/two_factor')
);

interface TwoFactorBody {
  authenticationCode: string;
  otpSecret: string;
}

export interface BackupCodes {
  backupCodes: string[];
}

export const activateTwoFactor = factory(
  (r) => (codes: TwoFactorBody) =>
    r<BackupCodes>('post', 'v2/player/two_factor', {
      two_factor: snakify({ ...codes }),
    })
);

export const deactivateTwoFactor = factory(
  (r) => (authentication_code: string) =>
    r<void>('delete', 'v2/player/two_factor', {
      two_factor: { authentication_code },
    })
);

// GAMES

export interface IHistoryGame {
  currency: CurrencyCode;
  totalBets: number;
  totalWins: number;
  game: string;
  title: string;
  provider: string;
  createdAt: string;
  finishedAt: string;
}

export const getHistoryGames = factory(
  (r) => () => r<IHistoryGame[]>('get', 'player/games')
);

// REWARDS

export interface IRewardHistory {
  createdAt: string;
  currency: string;
  eventType: string;
  id: number;
  prize: string | null;
  provider: string;
  amountCents: string;
  wagerCents: string;
}

export const getRewardsHistory = factory(
  (r) => () => r<IRewardHistory[]>('get', 'player/promo_events')
);

// USER LIMITS

export interface LimitAccount {
  currency: CurrencyCode;
  amountCents: number;
  currentValueAmountCents: number;
  activeUntil: string;
}

export enum UserLimitStatus {
  Active = 'active',
  Confirmed = 'confirmed',
  Unconfirmed = 'unconfirmed',
  UnconfirmedDisable = 'unconfirmed_disable',
  ConfirmedDisable = 'confirmed_disable',
}

export type LimitPeriod = 'day' | 'week' | 'month';

export enum LimitType {
  Deposit = 'deposit',
  Loss = 'loss',
  CoolingOff = 'coolingoff',
  SelfExclusion = 'selfexclusion',
  Wager = 'wager',
}

export interface Limit {
  period: LimitPeriod;
  status: UserLimitStatus;
  disableAt: string | null;
  confirmUntil: string | null;
  parent_type: 'accounts';
  strategy: 'accounts';
  created_at: string;
  id: number;
  type: LimitType;
  accounts: LimitAccount[];
}

export const getUserLimits = factory(
  (r) => () => r<Limit[]>('get', 'user_limits')
);

export const addUserLimit = factory(
  (r) => (user_limit: any) =>
    r<any>('post', 'user_limits', snakify({ user_limit }))
);

export const deleteUserLimit = factory(
  (r) => (id: number) => r<any>('delete', `user_limits/${id}`)
);

// PAYMENTS

export interface Transaction {
  id: number;
  amountCents: number;
  currency: CurrencyCode;
  action: Payment.Action;
  paymentSystem: string;
  recallable: boolean;
  createdAt: string;
  finishedAt: string;
  systemName: string;
  state: TransactionState;
  brand?: string;
  success: boolean;
  cancellationReason: string;
}

export enum PaymentStatusFilter {
  Pending = 'pending',
  Success = 'success',
  Failure = 'failure',
}

export interface PaymentsFilter {
  currency?: CurrencyCode;
  type?: Payment.Action;
  from?: string;
  to?: string;
  statuses?: PaymentStatusFilter[];
}

export interface PaymentsPageBody {
  page?: number;
  pageSize?: number;
  filter?: PaymentsFilter;
}

export interface PaymentsPagination {
  page: number;
  perPage: number;
  totalCount: number;
}

export interface PaymentsPageResponse {
  data: Transaction[];
  pagination: PaymentsPagination;
}

const PAYMENTS_PAGE_URI = 'player/payments/with_pages';
const PAYMENTS_PAGE_CONFIG = { headers: { 'X-Content-Policy': '4' } };

const MAX_PAGE_SIZE = 100;

function compactFilter(filter?: PaymentsFilter): PaymentsFilter | undefined {
  if (!filter) return;

  const compacted = Object.entries(filter).reduce<Record<string, unknown>>(
    (accumulator, [key, value]) => {
      if (value !== undefined) accumulator[key] = value;
      return accumulator;
    },
    {}
  );

  return Object.keys(compacted).length
    ? (compacted as PaymentsFilter)
    : undefined;
}

async function fetchPaymentsPage(
  r: typeof request,
  { filter, ...rest }: PaymentsPageBody
) {
  const { data } = await r<PaymentsPageResponse>(
    'post',
    PAYMENTS_PAGE_URI,
    snakify({ ...rest, filter: compactFilter(filter) }),
    PAYMENTS_PAGE_CONFIG
  );

  if (!(Array.isArray(data?.data) && data.pagination)) {
    throw new Error(`${PAYMENTS_PAGE_URI} answered with an unexpected body`);
  }

  return data;
}

export const getPlayerPaymentsPage = factory(
  (r) =>
    async (body: PaymentsPageBody = {}) => ({
      data: await fetchPaymentsPage(r, body),
    })
);

export const getPlayerCashoutsSince = factory((r) => async (from: string) => {
  const fetchPage = (page: number) =>
    fetchPaymentsPage(r, {
      page,
      pageSize: MAX_PAGE_SIZE,
      filter: { from, type: Payment.Action.Cashout },
    });

  const firstPage = await fetchPage(1);
  const pageCount = Math.ceil(firstPage.pagination.totalCount / MAX_PAGE_SIZE);

  const restPages = await Promise.all(
    Array.from({ length: Math.max(pageCount - 1, 0) }, (_, index) =>
      fetchPage(index + 2)
    )
  );

  return { data: [firstPage, ...restPages].flatMap(({ data }) => data) };
});

export const recallWithdraw = factory(
  (r) => (id: number) => r<void>('post', `player/payments/${id}/recall`)
);

export interface ReferralCode {
  refcode: string;
  claimedAmount: number;
  completedUsersCount: number;
  toClaimAmount: number;
  usersCount: number;
}

interface AggregatedData {
  totalInvited: number;
  totalClaimed: number;
}

export interface ReferralSystem {
  referralCodes: ReferralCode[];
  aggregatedData: AggregatedData;
  currency: string;
}

export const playerReferralSystem = factory(
  (r) => (currency: string) =>
    r<ReferralSystem>('get', `player/referral_system/list?currency=${currency}`)
);

export const createReferralCode = factory(
  (r) => (refCode: string) =>
    r<BackupCodes>('post', 'player/referral_system', snakify({ refCode }))
);

export const claimReferralCode = factory(
  (r) => (refCode: string) =>
    r<BackupCodes>('post', 'player/referral_system/claim', snakify({ refCode }))
);
